import { Link, usePage, Head } from "@inertiajs/react"
import { Badge } from "../components/ui/badge"
import { Button } from "../components/ui/button"
import { ArrowLeft, Calendar, Clock } from "lucide-react"
import { ReactNode } from "react"
import Layout from "../layouts/Layout"
import MarkdownPreview from '@uiw/react-markdown-preview'
import { useTheme } from "../layouts/ThemeProvider"
import { BlogPost as BlogPostType } from "../types/blog"
import ShareArticle from "../components/ShareArticle"

// Extend the PageProps interface for TypeScript
declare module '@inertiajs/core' {
  interface PageProps {
    post: BlogPostType
  }
}

const BlogPost = () => {
  const { theme } = useTheme()
  const { post } = usePage().props

  if (!post) {
    return (
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-2xl font-bold mb-4">Blog Post Not Found</h1>
          <p className="text-muted-foreground mb-8">The blog post you're looking for doesn't exist.</p>
          <Button asChild>
            <Link href="/blog">Back to Blog</Link>
          </Button>
        </div>
      </div>
    )
  }

  return (
    <>
      <Head title={`Codeware - ${post.title}`}>
        <meta name="description" content={post.excerpt || post.title} />
      </Head>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <Button variant="ghost" asChild className="mb-8">
            <Link href="/blog">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blog
            </Link>
          </Button>

          {/* Article Header */}
          <header className="mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6 leading-tight">
              {post.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-6 mb-6 text-muted-foreground">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {new Date(post.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                {post.readTime}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
              {post.tags_string?.split(',').map((tag) => (
                <Badge key={tag} variant="secondary">
                  {tag}
                </Badge>
              ))}
            </div>

            <div className="mb-8">
              <ShareArticle
                url={window.location.href}
                title={post.title}
                description={post.excerpt || post.title}
              />
            </div>
          </header>

          {/* Article Content */}
          <article className="prose prose-lg dark:prose-invert max-w-none">
            <MarkdownPreview 
              source={post.content}
              style={{ padding: 0 }}
              className="!bg-transparent"
              wrapperElement={{
                "data-color-mode": theme === "system" ? "light" : theme
              }}
            />
          </article>

          {/* Article Footer */}
          <footer className="mt-12 pt-8 border-t">
            <div className="flex flex-col gap-6">
              <div>
                <p className="text-muted-foreground mb-4">Found this article helpful? Share it with others!</p>
                <ShareArticle
                  url={window.location.href}
                  title={post.title}
                  description={post.excerpt || post.title}
                  className="mb-6"
                />
              </div>
              <div className="flex justify-end">
                <Button asChild>
                  <Link href="/contact">Get in Touch</Link>
                </Button>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </>
  )
}

BlogPost.layout = (page: ReactNode) => <Layout>{page}</Layout>

export default BlogPost